import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { collection, onSnapshot, query, deleteDoc, doc, orderBy, addDoc, Timestamp } from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from '../firebase';
import { UserProfile } from '../types';
import { 
  User, 
  Calendar, 
  ChevronDown, 
  ChevronUp, 
  Trash2, 
  ClipboardList, 
  FileText, 
  Lock,
  Clock,
  Briefcase,
  Eye,
  Printer,
  Send,
  X,
  Building,
  Mail,
  Check,
  Download
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { GenerateReportTrigger } from './GenerateReportTrigger';

interface UserTaskReportsTabProps {
  allUsers: UserProfile[];
  profile: UserProfile | null;
  setConfirmModal: (modal: {
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }) => void;
}

export const UserTaskReportsTab: React.FC<UserTaskReportsTabProps> = ({
  allUsers,
  profile,
  setConfirmModal,
}) => {
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [userTaskReports, setUserTaskReports] = useState<any[]>([]);
  const [expandedReportIds, setExpandedReportIds] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);

  // New states for Preview, PDF and Email actions
  const [previewReport, setPreviewReport] = useState<any | null>(null);
  const [shouldPrintAfterRender, setShouldPrintAfterRender] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState<any | null>(null);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailRecipients, setEmailRecipients] = useState('management@company.com');
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [branding, setBranding] = useState<any>(null);

  const formatTimestamp = (ts: any) => {
    if (!ts) return null;
    if (typeof ts.toDate === 'function') return ts.toDate();
    if (ts instanceof Date) return ts;
    if (ts.seconds) return new Date(ts.seconds * 1000);
    return new Date(ts);
  };

  const getRoleLabel = (role: string) => {
    if (!role) return 'Staff Member';
    switch (role.toLowerCase()) {
      case 'admin': return 'Administrator';
      case 'manager': return 'Manager / HOD';
      case 'md': return 'Managing Director';
      case 'technician': return 'Field Technician';
      case 'ops_manager': return 'Operations Manager';
      case 'hr': return 'HR Officer';
      default: return role.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    }
  };

  // Safe printed page activation and printing flow inside preview-aware useEffect
  useEffect(() => {
    if (previewReport && shouldPrintAfterRender) {
      // Add orientation styles dynamically for landscape
      const styleEl = document.createElement('style');
      styleEl.innerHTML = `@media print { @page { size: landscape; margin: 10mm; } }`;
      document.head.appendChild(styleEl);

      document.body.classList.add('print-user-report-active');

      const timer = setTimeout(() => {
        window.print();
        document.body.classList.remove('print-user-report-active');
        if (document.head.contains(styleEl)) {
          document.head.removeChild(styleEl);
        }
        setShouldPrintAfterRender(false);
      }, 600); // 600ms to guarantee complete DOM paint/re-render of the print target

      return () => {
        clearTimeout(timer);
        document.body.classList.remove('print-user-report-active');
        if (document.head.contains(styleEl)) {
          document.head.removeChild(styleEl);
        }
      };
    }
  }, [previewReport, shouldPrintAfterRender]);

  // Default to the currently logged in user
  useEffect(() => {
    if (profile?.uid && !selectedUserId) {
      setSelectedUserId(profile.uid);
    }
  }, [profile, selectedUserId]);

  // Subscribe to settings to load real company details / branding
  useEffect(() => {
    const q = query(collection(db, 'settings'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (!snapshot.empty) {
        setBranding(snapshot.docs[0].data());
      }
    }, (error) => {
      console.error("Error loading settings branding in reports tab:", error);
    });
    return unsubscribe;
  }, []);

  // Subscribe to 'user_task_reports' collection
  useEffect(() => {
    const q = query(collection(db, 'user_task_reports'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setUserTaskReports(snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() })));
      setLoading(false);
    }, (error) => {
      console.error("Error loading user_task_reports:", error);
      setLoading(false);
      handleFirestoreError(error, OperationType.LIST, 'user_task_reports');
    });
    return unsubscribe;
  }, []);

  // Check leader authorization
  const isLeader = !!(
    profile?.role === 'admin' || 
    profile?.role === 'manager' || 
    profile?.role === 'hr' || 
    profile?.isHOD === true || 
    profile?.role === 'ops_manager' || 
    profile?.role === 'md' || 
    profile?.role === 'technical'
  );

  const isOwnReport = selectedUserId === profile?.uid;
  const hasAccess = isLeader || isOwnReport;

  const toggleExpand = (reportId: string) => {
    setExpandedReportIds(prev => ({
      ...prev,
      [reportId]: !prev[reportId]
    }));
  };

  const handleDeleteReport = (reportId: string) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Tasks Report',
      message: 'Are you sure you want to delete this generated tasks report? This action is permanent and cannot be undone.',
      onConfirm: async () => {
        try {
          await deleteDoc(doc(db, 'user_task_reports', reportId));
          toast.success('Tasks report deleted successfully');
        } catch (error) {
          console.error("Error deleting report:", error);
          toast.error('Failed to delete tasks report');
          handleFirestoreError(error, OperationType.DELETE, `user_task_reports/${reportId}`);
        }
      }
    });
  };

  const handlePreviewReport = (report: any) => {
    setPreviewReport(report);
  };

  const handlePrintReport = (report: any) => {
    setPreviewReport(report);
    setShouldPrintAfterRender(true);
    toast.info('Preparing landscape document for PDF download/print...', { duration: 1500 });
  };

  const handleDownloadPDF = async (report: any, keepOpen: boolean = true) => {
    if (!report) return;
    setIsDownloadingPdf(true);
    const loadingToast = toast.loading('Generating high-quality PDF. Please wait...', { duration: 15000 });
    
    // Remember if it was already open
    const wasAlreadyOpen = !!previewReport;
    
    // Helper to temporarily remove oklch colors from stylesheets
    const preprocessStylesheets = () => {
      const restores: Array<() => void> = [];
      try {
        // 1. Process inline style elements
        const styleElements = Array.from(document.querySelectorAll('style'));
        for (const styleEl of styleElements) {
          const originalText = styleEl.textContent;
          if (originalText && originalText.includes('oklch')) {
            const modifiedText = originalText.replace(/oklch\([^)]+\)/g, 'rgb(120, 120, 120)');
            styleEl.textContent = modifiedText;
            restores.push(() => {
              styleEl.textContent = originalText;
            });
          }
        }

        // 2. Process link stylesheets
        const linkElements = Array.from(document.querySelectorAll('link[rel="stylesheet"]')) as HTMLLinkElement[];
        for (const linkEl of linkElements) {
          try {
            const sheet = linkEl.sheet;
            if (sheet) {
              const rules = Array.from(sheet.cssRules);
              let cssText = '';
              for (const rule of rules) {
                cssText += rule.cssText + '\n';
              }
              if (cssText.includes('oklch')) {
                const modifiedText = cssText.replace(/oklch\([^)]+\)/g, 'rgb(120, 120, 120)');
                const newStyleEl = document.createElement('style');
                newStyleEl.textContent = modifiedText;
                
                linkEl.parentNode?.insertBefore(newStyleEl, linkEl);
                linkEl.disabled = true;
                
                restores.push(() => {
                  newStyleEl.parentNode?.removeChild(newStyleEl);
                  linkEl.disabled = false;
                });
              }
            }
          } catch (e) {
            // Ignore cross-origin stylesheet errors
          }
        }
      } catch (err) {
        console.error('Error preprocessing stylesheets:', err);
      }

      return () => {
        for (let i = restores.length - 1; i >= 0; i--) {
          try {
            restores[i]();
          } catch (e) {
            console.error('Error restoring stylesheet:', e);
          }
        }
      };
    };

    let restoreStyles: (() => void) | null = null;
    
    try {
      if (!wasAlreadyOpen) {
        setPreviewReport(report);
      }
      
      // Wait for React to render and paint the element in DOM
      await new Promise(resolve => setTimeout(resolve, 800));
      
      const element = document.getElementById('printable-report');
      if (!element) {
        toast.dismiss(loadingToast);
        toast.error('Failed to locate printable report layout.');
        setIsDownloadingPdf(false);
        if (!wasAlreadyOpen) {
          setPreviewReport(null);
        }
        return;
      }
      
      // Clean up stylesheets right before html2canvas parses them
      restoreStyles = preprocessStylesheets();
      
      // html2canvas config
      const canvas = await html2canvas(element, {
        scale: 2, // High resolution
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: 1200, // standard desktop width to ensure fully-expanded non-mobile styles
        height: element.scrollHeight,
        windowHeight: element.scrollHeight,
      });
      
      // Restore styles immediately after html2canvas completes parsing the DOM and styles
      if (restoreStyles) {
        restoreStyles();
        restoreStyles = null;
      }
      
      const imgData = canvas.toDataURL('image/png');
      
      // Create landscape A4 document
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      
      // Calculate scaled dimensions to fit perfectly across the landscape A4 width (297mm)
      const pxToMm = pdfWidth / imgWidth;
      const imgScaledHeight = imgHeight * pxToMm;
      
      if (imgScaledHeight <= pdfHeight) {
        // Fits perfectly on a single page, center it vertically
        const yOffset = (pdfHeight - imgScaledHeight) / 2;
        pdf.addImage(imgData, 'PNG', 0, yOffset, pdfWidth, imgScaledHeight);
        
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(8);
        pdf.setTextColor(150, 150, 150);
        pdf.text('Page 1 of 1', pdfWidth - 25, pdfHeight - 8);
      } else {
        // Multi-page document - slice across multiple landscape A4 pages (210mm height each)
        const totalPages = Math.ceil(imgScaledHeight / pdfHeight);
        
        for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
          if (pageNum > 1) {
            pdf.addPage();
          }
          
          // Draw the image shifted up by the height of previous pages
          const yOffset = - (pageNum - 1) * pdfHeight;
          pdf.addImage(imgData, 'PNG', 0, yOffset, pdfWidth, imgScaledHeight);
          
          // Render elegant footer with correct page numbers
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(8);
          pdf.setTextColor(150, 150, 150);
          pdf.text(`Page ${pageNum} of ${totalPages}`, pdfWidth - 25, pdfHeight - 8);
        }
      }
      
      const safeName = report.userName.replace(/[^a-zA-Z0-9]/g, '_');
      const startD = formatTimestamp(report.startDate) || new Date();
      const dateStr = startD.toISOString().split('T')[0];
      const fileName = `Task_Report_${safeName}_${report.periodType}_${dateStr}.pdf`;
      
      pdf.save(fileName);
      
      toast.dismiss(loadingToast);
      toast.success('PDF downloaded successfully!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.dismiss(loadingToast);
      toast.error('Failed to generate PDF document.');
    } finally {
      if (restoreStyles) {
        try {
          restoreStyles();
        } catch (e) {
          console.error(e);
        }
      }
      setIsDownloadingPdf(false);
      // If we opened it just for printing, let's close it now
      if (!wasAlreadyOpen && !keepOpen) {
        setPreviewReport(null);
      }
    }
  };

  const handleEmailManagementClick = (report: any) => {
    const startD = formatTimestamp(report.startDate) instanceof Date && !isNaN((formatTimestamp(report.startDate) as Date).getTime()) ? formatTimestamp(report.startDate) : new Date();
    const endD = formatTimestamp(report.endDate) instanceof Date && !isNaN((formatTimestamp(report.endDate) as Date).getTime()) ? formatTimestamp(report.endDate) : new Date();
    const createdD = formatTimestamp(report.createdAt);
    const createdDStr = createdD instanceof Date && !isNaN(createdD.getTime()) ? createdD.toLocaleString() : new Date().toLocaleString();

    const emailRoleObj = allUsers.find(u => u.uid === report.userId);
    const userRoleStr = getRoleLabel(emailRoleObj?.role || report.userRole || 'Staff Member');

    setShowEmailModal(report);
    setEmailSubject(`Staff Performance Activity Report: ${report.userName} (${report.periodType.toUpperCase()})`);
    
    const managementEmails = allUsers
      .filter(u => ['admin', 'manager', 'md', 'ops_manager', 'hr'].includes(u.role || ''))
      .map(u => u.email)
      .join(', ') || 'management@company.com';

    setEmailRecipients(managementEmails);
    setEmailBody(`Dear Management Team,

Please find attached the activities and tasks performance report for staff member ${report.userName} (${userRoleStr}) for the period under review.

Summary Details:
-------------------------------------------
- Staff Member: ${report.userName}
- Period Range: ${startD.toLocaleDateString()} to ${endD.toLocaleDateString()}
- Total Tasks/Activities Performed: ${report.tasksCount}
- Generated On: ${createdDStr}

The items in report printout have been formatted into tables in a Landscape orientation matching our company letterhead. You can view the fully detailed interactive version within the Performance portal.

Best Regards,
System Automated Dispatch
Customer Care & Staff Performance Portal`);
  };

  const handleSendEmail = async () => {
    if (!showEmailModal) return;
    setIsSendingEmail(true);
    try {
      // 1. Log dispatch in Firebase
      await addDoc(collection(db, 'email_dispatches'), {
        reportId: showEmailModal.id,
        staffId: showEmailModal.userId,
        staffName: showEmailModal.userName,
        sentBy: profile?.email || 'System',
        sentById: profile?.uid || 'system',
        recipients: emailRecipients,
        subject: emailSubject,
        body: emailBody,
        status: 'delivered',
        sentAt: new Date().toISOString()
      });

      // 2. Dispatch real email via SMTP endpoint
      try {
        const response = await fetch('/api/send-email', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            to: emailRecipients,
            subject: emailSubject,
            body: emailBody
          })
        });

        if (!response.ok) {
          const errResponse = await response.json().catch(() => ({}));
          console.error("SMTP delivery response error:", errResponse);
        }
      } catch (smtpError) {
        console.error("Error connecting to email API:", smtpError);
      }

      // 3. Create real in-app notifications for admins & managers (instead of userId: 'all' which is unqueryable)
      const managementUserIds = allUsers
        .filter(u => ['admin', 'manager', 'md', 'ops_manager', 'hr'].includes(u.role || ''))
        .map(u => u.uid)
        .filter(Boolean);

      // Create a notification for each management user
      const notifyPromises = managementUserIds.map(userId => 
        addDoc(collection(db, 'notifications'), {
          userId,
          title: 'Report Emailed to Management',
          message: `${profile?.displayName || 'A user'} has sent the performance report of ${showEmailModal.userName} to management.`,
          type: 'report',
          read: false,
          createdAt: Timestamp.now()
        })
      );
      
      // Also notify the sender if they are not in the management list
      if (profile?.uid && !managementUserIds.includes(profile.uid)) {
        notifyPromises.push(
          addDoc(collection(db, 'notifications'), {
            userId: profile.uid,
            title: 'Report Emailed to Management',
            message: `You successfully sent the performance report of ${showEmailModal.userName} to management.`,
            type: 'report',
            read: false,
            createdAt: Timestamp.now()
          })
        );
      }

      await Promise.all(notifyPromises.map(p => p.catch(err => console.warn("Failed to write notification:", err))));

      toast.success('Performance report successfully emailed to management!');
      setShowEmailModal(null);
    } catch (error) {
      console.error("Error logging email dispatch:", error);
      toast.error('Failed to dispatch email');
    } finally {
      setIsSendingEmail(false);
    }
  };

  // Filter reports for selected user
  const filteredReports = userTaskReports.filter(r => r.userId === selectedUserId);

  const pStartDate = previewReport ? formatTimestamp(previewReport.startDate) : null;
  const pEndDate = previewReport ? formatTimestamp(previewReport.endDate) : null;
  const pCreatedAt = previewReport ? formatTimestamp(previewReport.createdAt) : null;

  const previewStartDateStr = pStartDate instanceof Date && !isNaN(pStartDate.getTime()) ? pStartDate.toLocaleDateString() : 'N/A';
  const previewEndDateStr = pEndDate instanceof Date && !isNaN(pEndDate.getTime()) ? pEndDate.toLocaleDateString() : 'N/A';
  const previewCreatedAtStr = pCreatedAt instanceof Date && !isNaN(pCreatedAt.getTime()) ? pCreatedAt.toLocaleString() : 'N/A';

  const previewUserObj = previewReport ? allUsers.find(u => u.uid === previewReport.userId) : null;
  const previewUserRoleStr = previewReport ? getRoleLabel(previewUserObj?.role || previewReport.userRole || 'Staff Member') : '';

  if (loading) {
    return <div className="flex items-center justify-center py-12 text-slate-500">Loading task reports...</div>;
  }

  return (
    <div className="space-y-6">
      {/* User Selector Header */}
      <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <User className="w-5 h-5 text-orange-500" />
            Select User to View Reports
          </h3>
          <p className="text-xs text-slate-500 mt-1">Choose a team member from the dropdown to see their generated task summaries</p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <select
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value)}
            className="w-full md:w-80 px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-orange-500/20 transition-all font-semibold text-slate-700 cursor-pointer"
          >
            <option value="">Select a user</option>
            {allUsers.map((user) => (
              <option key={user.uid} value={user.uid}>
                {user.displayName || user.email} ({user.role})
              </option>
            ))}
          </select>

          {selectedUserId && (
            <GenerateReportTrigger 
              targetUserId={selectedUserId}
              allUsers={allUsers}
              className="flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-5 py-3 rounded-2xl font-bold transition-all shadow-lg shadow-orange-200 text-xs cursor-pointer h-12 shrink-0"
            />
          )}
        </div>
      </div>

      {/* Access Denied Shield */}
      {!hasAccess ? (
        <div className="flex flex-col items-center justify-center p-16 bg-white rounded-[32px] border border-red-50 shadow-lg text-center">
          <div className="w-20 h-20 bg-red-50 text-red-500 rounded-3xl flex items-center justify-center mb-6 shadow-sm">
            <Lock className="w-10 h-10" />
          </div>
          <h3 className="text-2xl font-black text-slate-900 tracking-tight">Access Denied</h3>
          <p className="text-slate-500 mt-2 max-w-md font-medium text-sm leading-relaxed">
            You do not have permission to view reports for other team members. 
            Only **Heads of Departments, Managers, HR, Administrators, and other leaders** are authorized to access this information.
          </p>
        </div>
      ) : (
        /* Reports Container */
        <div className="space-y-4">
          {filteredReports.length === 0 ? (
            <div className="text-center p-16 bg-white rounded-[32px] border border-slate-100 shadow-sm text-slate-500 flex flex-col items-center justify-center">
              <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center text-slate-300 mx-auto mb-4">
                <ClipboardList className="w-8 h-8" />
              </div>
              <p className="font-bold text-slate-700 text-lg">No task reports generated yet</p>
              <p className="text-sm text-slate-400 mt-1 max-w-sm mx-auto mb-6">
                No reports have been generated for this user in the selected periods. You can generate one right now using the button below.
              </p>
              {selectedUserId && (
                <GenerateReportTrigger 
                  targetUserId={selectedUserId}
                  allUsers={allUsers}
                  className="flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-2xl font-bold transition-all shadow-lg shadow-orange-100 text-sm cursor-pointer"
                />
              )}
            </div>
          ) : (
            filteredReports.map((report) => {
              const isExpanded = !!expandedReportIds[report.id];
              const reportDate = report.createdAt?.toDate ? report.createdAt.toDate() : new Date(report.createdAt);
              const startDateStr = report.startDate?.toDate ? report.startDate.toDate().toLocaleDateString() : new Date(report.startDate).toLocaleDateString();
              const endDateStr = report.endDate?.toDate ? report.endDate.toDate().toLocaleDateString() : new Date(report.endDate).toLocaleDateString();

              return (
                <div 
                  key={report.id}
                  className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-300"
                >
                  {/* Report Card Header */}
                  <div className="p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-slate-50/50">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-orange-50 text-orange-600 rounded-2xl flex items-center justify-center shrink-0">
                        <FileText className="w-6 h-6" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-black text-slate-900 text-lg tracking-tight">
                            {report.periodType.toUpperCase()} Task Report
                          </h4>
                          <span className="px-2.5 py-0.5 bg-orange-100 text-orange-700 rounded-full text-[10px] font-bold uppercase tracking-wider">
                            {report.tasksCount} Tasks
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-slate-500 text-xs mt-1.5 flex-wrap">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5 text-slate-400" />
                            <span>Range: {startDateStr} - {endDateStr}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5 text-slate-400" />
                            <span>Generated: {reportDate.toLocaleDateString()}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                     <div className="flex items-center gap-2 flex-wrap justify-end">
                      <button
                        onClick={() => handlePreviewReport(report)}
                        className="flex items-center gap-1.5 px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-xl text-xs font-bold transition-all shadow-sm cursor-pointer"
                        title="Preview Landscape Document"
                      >
                        <Eye className="w-4 h-4 text-slate-600" />
                        Preview
                      </button>

                      <button
                        onClick={() => handleDownloadPDF(report, false)}
                        disabled={isDownloadingPdf}
                        className="flex items-center gap-1.5 px-3 py-2 bg-emerald-50 hover:bg-emerald-100 disabled:bg-slate-100 disabled:text-slate-400 text-emerald-700 border border-emerald-100 rounded-xl text-xs font-bold transition-all shadow-sm cursor-pointer"
                        title="Download Clean Landscape PDF File"
                      >
                        <Download className="w-4 h-4 text-emerald-600" />
                        Download PDF
                      </button>

                      <button
                        onClick={() => handleEmailManagementClick(report)}
                        className="flex items-center gap-1.5 px-3 py-2 bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-100 rounded-xl text-xs font-bold transition-all shadow-sm cursor-pointer"
                        title="Email Performance Report to Management"
                      >
                        <Send className="w-4 h-4 text-purple-600" />
                        Email to Management
                      </button>

                      <button
                        onClick={() => toggleExpand(report.id)}
                        className="flex items-center gap-1.5 px-4 py-2 bg-white border border-slate-200 hover:border-slate-300 text-slate-700 rounded-xl text-xs font-bold transition-all shadow-sm cursor-pointer"
                      >
                        {isExpanded ? (
                          <>
                            <ChevronUp className="w-4 h-4" />
                            Collapse
                          </>
                        ) : (
                          <>
                            <ChevronDown className="w-4 h-4" />
                            View Tasks
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => handleDeleteReport(report.id)}
                        className="p-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl transition-all shadow-sm cursor-pointer"
                        title="Delete this generated report"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Tasks List Dropdown */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="border-t border-slate-100 bg-white"
                      >
                        <div className="p-6 space-y-4">
                          <h5 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1.5 mb-2">
                            <Briefcase className="w-3.5 h-3.5" />
                            Tasks Performed List
                          </h5>
                          {report.tasks && report.tasks.length > 0 ? (
                            <div className="grid grid-cols-1 gap-3">
                              {report.tasks.map((task: any, index: number) => (
                                <div 
                                  key={task.id || index}
                                  className="p-4 bg-slate-50/50 hover:bg-slate-50 rounded-2xl border border-slate-100/80 hover:border-slate-100 flex flex-col md:flex-row justify-between gap-4 transition-colors"
                                >
                                  <div className="space-y-1.5">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${
                                        task.type === 'Technical Task' ? 'bg-blue-100 text-blue-700' :
                                        task.type === 'Technical Service Report' ? 'bg-emerald-100 text-emerald-700' :
                                        task.type === 'Customer Care Task' ? 'bg-orange-100 text-orange-700' :
                                        task.type === 'Customer Care Tech Report' ? 'bg-purple-100 text-purple-700' :
                                        task.type === 'Billing Invoice' ? 'bg-cyan-100 text-cyan-700' :
                                        task.type === 'Inventory Transaction' ? 'bg-indigo-100 text-indigo-700' :
                                        task.type === 'Cash Requisition' ? 'bg-amber-100 text-amber-700' :
                                        'bg-slate-100 text-slate-700'
                                      }`}>
                                        {task.type}
                                      </span>
                                      <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${
                                        ['completed', 'approved', 'paid', 'released'].includes(task.status?.toLowerCase()) ? 'bg-green-100 text-green-700' :
                                        ['pending', 'draft', 'in-progress', 'submitted_to_technical', 'arranged_by_technical'].includes(task.status?.toLowerCase()) ? 'bg-amber-100 text-amber-700' :
                                        'bg-slate-100 text-slate-700'
                                      }`}>
                                        {task.status}
                                      </span>
                                    </div>
                                    <h4 className="font-bold text-slate-800 text-sm">{task.title}</h4>
                                    <p className="text-xs text-slate-500">
                                      <span className="font-medium text-slate-600">Client:</span> {task.clientName}
                                    </p>
                                    {task.description && (
                                      <p className="text-xs text-slate-500 italic bg-white p-3 rounded-xl border border-slate-100 mt-2 leading-relaxed">
                                        "{task.description}"
                                      </p>
                                    )}
                                  </div>
                                  <div className="text-left md:text-right shrink-0 flex flex-col justify-between">
                                    <div>
                                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Date Performed</p>
                                      <p className="text-xs font-semibold text-slate-600 mt-0.5">
                                        {new Date(task.createdAt).toLocaleDateString()}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-slate-500 italic p-4 text-center bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                              No details recorded for tasks in this period.
                            </p>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Landscape Print Styles */}
      <style>{`
        @media print {
          /* Hide all default page elements */
          body * {
            visibility: hidden !important;
          }
          
          /* Keep only our print wrapper and its descendants visible */
          .print-wrapper-target, .print-wrapper-target * {
            visibility: visible !important;
          }
          
          /* Position the print target to overlay perfectly at the top-left */
          .print-wrapper-target {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            height: auto !important;
            margin: 0 !important;
            padding: 10mm !important;
            border: none !important;
            box-shadow: none !important;
            background: white !important;
            color: black !important;
            z-index: 9999999 !important;
            display: block !important;
          }

          /* Ensure body and html are properly visible and white */
          html, body {
            background: white !important;
            color: black !important;
            visibility: visible !important;
            height: auto !important;
            overflow: visible !important;
          }

          /* Make sure parent modal wrappers do not render backgrounds or disrupt positions */
          .no-print, .fixed, .absolute, #root {
            background: transparent !important;
            backdrop-filter: none !important;
            border: none !important;
            box-shadow: none !important;
            position: static !important;
            height: 0 !important;
            min-height: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
            overflow: visible !important;
          }

          /* Force Landscape orientation */
          @page {
            size: landscape;
            margin: 10mm 15mm 10mm 15mm;
          }

          /* Hide modal close or action buttons when printing */
          .print-action-bar {
            display: none !important;
          }

          /* Table border adjustments for print */
          table {
            border-collapse: collapse !important;
            width: 100% !important;
          }
          th, td {
            border: 1px solid #cbd5e1 !important;
            padding: 8px !important;
          }
        }
      `}</style>

      {/* 1. Preview Report Modal */}
      <AnimatePresence>
        {previewReport && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto bg-slate-900/60 backdrop-blur-sm no-print">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setPreviewReport(null)}
              className="absolute inset-0"
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-6xl bg-white rounded-3xl shadow-2xl overflow-hidden border border-slate-100 flex flex-col my-8 max-h-[90vh] z-10"
            >
              {/* Modal Control Header */}
              <div className="p-4 bg-slate-900 text-white flex items-center justify-between print-action-bar shrink-0">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center text-white font-bold text-sm">
                    PR
                  </div>
                  <div>
                    <h3 className="font-bold text-sm tracking-tight">Landscape Document Preview</h3>
                    <p className="text-[10px] text-slate-400">RAMIT SOLUTIONS LTD - Internal Performance Audit</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleDownloadPDF(previewReport, true)}
                    disabled={isDownloadingPdf}
                    className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-700 text-white rounded-xl text-xs font-bold transition-all shadow-md cursor-pointer"
                  >
                    <Download className="w-4 h-4" />
                    {isDownloadingPdf ? 'Generating PDF...' : 'Download PDF'}
                  </button>
                  <button
                    onClick={() => handlePrintReport(previewReport)}
                    className="flex items-center gap-1.5 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-xl text-xs font-bold transition-all shadow-md cursor-pointer"
                  >
                    <Printer className="w-4 h-4" />
                    Print via Browser
                  </button>
                  <button
                    onClick={() => handleEmailManagementClick(previewReport)}
                    className="flex items-center gap-1.5 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-xl text-xs font-bold transition-all shadow-md cursor-pointer"
                  >
                    <Send className="w-4 h-4" />
                    Email to Management
                  </button>
                  <button
                    onClick={() => setPreviewReport(null)}
                    className="p-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl transition-all cursor-pointer"
                    title="Close"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Scrollable Landscape Page Mockup */}
              <div className="p-8 overflow-y-auto bg-slate-100/50 flex-1 flex justify-center">
                {/* Physical landscape sheet container */}
                <div 
                  id="printable-report"
                  className="print-wrapper-target w-full max-w-[297mm] bg-white p-12 border border-slate-200 shadow-xl rounded-2xl relative min-h-[210mm] text-slate-800"
                >
                  {/* Company Letterhead */}
                  <div className="flex justify-between items-start border-b-2 border-slate-900 pb-6 mb-6">
                    <div className="flex items-center gap-5">
                      {branding?.logoUrl ? (
                        <img 
                          src={branding.logoUrl} 
                          alt="Company Logo" 
                          className="h-16 w-auto object-contain"
                          referrerPolicy="no-referrer"
                        />
                      ) : (
                        <div className="w-16 h-16 bg-slate-100 rounded-xl flex items-center justify-center text-slate-400 font-black text-xs border border-slate-200">
                          LOGO
                        </div>
                      )}
                      <div>
                        <h1 className="text-2xl font-black text-slate-900 tracking-tight uppercase leading-none">
                          {branding?.companyName || 'RAMIT SOLUTIONS LTD'}
                        </h1>
                        <p className="text-[10px] text-slate-500 font-mono mt-1">
                          TIN: {branding?.tin || '123-456-789'} | VRN: {branding?.vrn || '987-654-321'}
                        </p>
                      </div>
                    </div>

                    <div className="text-right text-[11px] text-slate-600 space-y-0.5 font-medium leading-relaxed">
                      <div className="flex items-center justify-end gap-1.5 text-slate-900 font-bold mb-1">
                        <Building className="w-3.5 h-3.5 text-slate-400" />
                        <span>Head Office</span>
                      </div>
                      <p>{branding?.companyAddress || 'Plot No. 42, Kijitonyama, Dar es Salaam, Tanzania'}</p>
                      <p>Tel: {branding?.companyPhone || '+255 700 000 000'}</p>
                      <p>Email: {branding?.companyEmail || 'info@ramitsolutions.com'} | Web: {branding?.companyWebsite || 'www.ramitsolutions.com'}</p>
                    </div>
                  </div>

                  {/* Title */}
                  <div className="text-center my-6">
                    <h2 className="text-lg font-black text-slate-900 uppercase tracking-widest border-b border-slate-200 pb-1.5 inline-block px-8">
                      Staff Performance & Activity Report
                    </h2>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">
                      Period: {previewReport.periodType} ({previewStartDateStr} - {previewEndDateStr})
                    </p>
                  </div>

                  {/* Metadata Block */}
                  <div className="grid grid-cols-4 gap-6 bg-slate-50 p-5 rounded-2xl border border-slate-100 mb-8 text-xs">
                    <div>
                      <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">Staff Member</span>
                      <span className="font-extrabold text-slate-800 text-sm mt-0.5 block">{previewReport.userName}</span>
                    </div>
                    <div>
                      <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">Designation / Role</span>
                      <span className="font-extrabold text-slate-800 text-sm mt-0.5 block uppercase tracking-wide">{previewUserRoleStr}</span>
                    </div>
                    <div>
                      <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">Report Generated</span>
                      <span className="font-extrabold text-slate-800 text-sm mt-0.5 block">
                        {previewCreatedAtStr}
                      </span>
                    </div>
                    <div>
                      <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">Total Tasks Performed</span>
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-orange-100 text-orange-800 rounded-full text-xs font-black mt-0.5">
                        {previewReport.tasksCount} Tasks
                      </span>
                    </div>
                  </div>

                  {/* Items Table */}
                  <div className="overflow-x-auto border border-slate-200 rounded-2xl mb-8">
                    <table className="w-full border-collapse text-left text-xs">
                      <thead>
                        <tr className="bg-slate-100 border-b border-slate-200 text-slate-700 uppercase text-[9px] font-black tracking-wider">
                          <th className="py-3 px-3 border-r border-slate-200 w-10 text-center">#</th>
                          <th className="py-3 px-3 border-r border-slate-200 w-24">Date</th>
                          <th className="py-3 px-3 border-r border-slate-200 w-40">Category / Type</th>
                          <th className="py-3 px-3 border-r border-slate-200 w-52">Activity Title</th>
                          <th className="py-3 px-3 border-r border-slate-200 w-36">Client / Site</th>
                          <th className="py-3 px-3 border-r border-slate-200 w-24 text-center">Status</th>
                          <th className="py-3 px-3">Details / Description</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200 text-slate-700">
                        {previewReport.tasks && previewReport.tasks.length > 0 ? (
                          previewReport.tasks.map((task: any, idx: number) => {
                            const taskDate = task.createdAt ? (formatTimestamp(task.createdAt)?.toLocaleDateString() || new Date(task.createdAt).toLocaleDateString()) : 'N/A';
                            return (
                              <tr key={task.id + '-' + idx} className="hover:bg-slate-50 transition-colors">
                                <td className="py-2.5 px-3 border-r border-slate-200 text-center font-bold text-slate-400">{idx + 1}</td>
                                <td className="py-2.5 px-3 border-r border-slate-200 whitespace-nowrap text-slate-600">{taskDate}</td>
                                <td className="py-2.5 px-3 border-r border-slate-200">
                                  <span className={`inline-block px-2 py-0.5 rounded-md text-[8px] font-bold uppercase tracking-wider ${
                                    task.type === 'Technical Task' ? 'bg-blue-100 text-blue-800' :
                                    task.type === 'Technical Service Report' ? 'bg-emerald-100 text-emerald-800' :
                                    task.type === 'Customer Care Task' ? 'bg-orange-100 text-orange-800' :
                                    task.type === 'Customer Care Tech Report' ? 'bg-purple-100 text-purple-800' :
                                    task.type === 'Billing Invoice' ? 'bg-cyan-100 text-cyan-800' :
                                    task.type === 'Inventory Transaction' ? 'bg-indigo-100 text-indigo-800' :
                                    task.type === 'Cash Requisition' ? 'bg-amber-100 text-amber-800' :
                                    'bg-slate-100 text-slate-800'
                                  }`}>
                                    {task.type}
                                  </span>
                                </td>
                                <td className="py-2.5 px-3 border-r border-slate-200 font-extrabold text-slate-900">{task.title}</td>
                                <td className="py-2.5 px-3 border-r border-slate-200 font-medium text-slate-600">{task.clientName || 'N/A'}</td>
                                <td className="py-2.5 px-3 border-r border-slate-200 text-center">
                                  <span className={`inline-block px-2 py-0.5 rounded-full text-[8px] font-bold uppercase tracking-widest ${
                                    ['completed', 'approved', 'paid', 'released'].includes(task.status?.toLowerCase())
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-amber-100 text-amber-800'
                                  }`}>
                                    {task.status || 'N/A'}
                                  </span>
                                </td>
                                <td className="py-2.5 px-3 text-slate-500 max-w-xs break-words">{task.description || 'No details.'}</td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan={7} className="py-8 text-center text-slate-400 italic">No tasks found for this period.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Signatures */}
                  <div className="grid grid-cols-3 gap-12 mt-12 pt-8 border-t border-slate-200 text-xs">
                    <div>
                      <p className="font-bold text-slate-400 uppercase tracking-widest text-[9px]">Prepared By (Staff)</p>
                      <div className="h-16 flex items-end border-b border-slate-300 pb-1">
                        <span className="text-slate-800 font-mono italic text-sm">{previewReport.userName}</span>
                      </div>
                      <p className="mt-2 text-slate-700 font-bold">{previewReport.userName}</p>
                      <p className="text-[10px] text-slate-400 capitalize">{previewUserRoleStr}</p>
                    </div>
                    <div>
                      <p className="font-bold text-slate-400 uppercase tracking-widest text-[9px]">Reviewed By (Department Head)</p>
                      <div className="h-16 flex items-end border-b border-slate-300 pb-1">
                        {/* Space for review signature */}
                      </div>
                      <p className="mt-2 text-slate-700 font-bold">HOD / Manager</p>
                      <p className="text-[10px] text-slate-400">Operations Team</p>
                    </div>
                    <div>
                      <p className="font-bold text-slate-400 uppercase tracking-widest text-[9px]">Approved By (Managing Director)</p>
                      <div className="h-16 flex items-end border-b border-slate-300 pb-1">
                        {/* Space for management signature */}
                      </div>
                      <p className="mt-2 text-slate-700 font-bold">Managing Director</p>
                      <p className="text-[10px] text-slate-400">RAMIT SOLUTIONS LTD</p>
                    </div>
                  </div>

                  {/* Footer Notice */}
                  <div className="text-center text-[9px] text-slate-400 font-medium mt-12 pt-4 border-t border-slate-100">
                    Confidential Report - Generated automatically by the Performance Evaluation & Audit system. 
                    Company Registered with BRELA Tanzania.
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {createPortal(
        previewReport && (
          <div className="print-only user-report-print-target p-8 bg-white text-black font-sans leading-relaxed">
            {/* Print Header/Letterhead */}
            <div className="flex justify-between items-start border-b-2 border-slate-900 pb-6 mb-6">
              <div className="flex items-center gap-5">
                {branding?.logoUrl ? (
                  <img 
                    src={branding.logoUrl} 
                    alt="Company Logo" 
                    className="h-16 w-auto object-contain"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <div className="w-16 h-16 bg-slate-100 rounded-xl flex items-center justify-center text-slate-400 font-black text-xs border border-slate-200">
                    LOGO
                  </div>
                )}
                <div>
                  <h1 className="text-2xl font-black text-slate-900 tracking-tight uppercase leading-none">
                    {branding?.companyName || 'RAMIT SOLUTIONS LTD'}
                  </h1>
                  <p className="text-[10px] text-slate-500 font-mono mt-1">
                    TIN: {branding?.tin || '123-456-789'} | VRN: {branding?.vrn || '987-654-321'}
                  </p>
                </div>
              </div>

              <div className="text-right text-[11px] text-slate-600 space-y-0.5 font-medium leading-relaxed">
                <div className="flex items-center justify-end gap-1.5 text-slate-900 font-bold mb-1">
                  <Building className="w-3.5 h-3.5 text-slate-400" />
                  <span>Head Office</span>
                </div>
                <p>{branding?.companyAddress || 'Plot No. 42, Kijitonyama, Dar es Salaam, Tanzania'}</p>
                <p>Tel: {branding?.companyPhone || '+255 700 000 000'}</p>
                <p>Email: {branding?.companyEmail || 'info@ramitsolutions.com'} | Web: {branding?.companyWebsite || 'www.ramitsolutions.com'}</p>
              </div>
            </div>

            {/* Title */}
            <div className="text-center my-6">
              <h2 className="text-lg font-black text-slate-900 uppercase tracking-widest border-b border-slate-200 pb-1.5 inline-block px-8">
                Staff Performance & Activity Report
              </h2>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">
                Period: {previewReport.periodType} ({previewStartDateStr} - {previewEndDateStr})
              </p>
            </div>

            {/* Metadata Block */}
            <div className="grid grid-cols-4 gap-6 bg-slate-50 p-5 rounded-2xl border border-slate-100 mb-8 text-xs">
              <div>
                <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">Staff Member</span>
                <span className="font-extrabold text-slate-800 text-sm mt-0.5 block">{previewReport.userName}</span>
              </div>
              <div>
                <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">Designation / Role</span>
                <span className="font-extrabold text-slate-800 text-sm mt-0.5 block uppercase tracking-wide">{previewUserRoleStr}</span>
              </div>
              <div>
                <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">Report Generated</span>
                <span className="font-extrabold text-slate-800 text-sm mt-0.5 block">
                  {previewCreatedAtStr}
                </span>
              </div>
              <div>
                <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">Total Tasks Performed</span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-orange-100 text-orange-800 rounded-full text-xs font-black mt-0.5">
                  {previewReport.tasksCount} Tasks
                </span>
              </div>
            </div>

            {/* Items Table */}
            <div className="overflow-x-auto border border-slate-200 rounded-2xl mb-8">
              <table className="w-full border-collapse text-left text-xs">
                <thead>
                  <tr className="bg-slate-100 border-b border-slate-200 text-slate-700 uppercase text-[9px] font-black tracking-wider">
                    <th className="py-3 px-3 border-r border-slate-200 w-10 text-center">#</th>
                    <th className="py-3 px-3 border-r border-slate-200 w-24">Date</th>
                    <th className="py-3 px-3 border-r border-slate-200 w-40">Category / Type</th>
                    <th className="py-3 px-3 border-r border-slate-200 w-52">Activity Title</th>
                    <th className="py-3 px-3 border-r border-slate-200 w-36">Client / Site</th>
                    <th className="py-3 px-3 border-r border-slate-200 w-24 text-center">Status</th>
                    <th className="py-3 px-3">Details / Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 text-slate-700">
                  {previewReport.tasks && previewReport.tasks.length > 0 ? (
                    previewReport.tasks.map((task: any, idx: number) => {
                      const taskDate = task.createdAt ? (formatTimestamp(task.createdAt)?.toLocaleDateString() || new Date(task.createdAt).toLocaleDateString()) : 'N/A';
                      return (
                        <tr key={task.id + '-' + idx} className="hover:bg-slate-50 transition-colors">
                          <td className="py-2.5 px-3 border-r border-slate-200 text-center font-bold text-slate-400">{idx + 1}</td>
                          <td className="py-2.5 px-3 border-r border-slate-200 whitespace-nowrap text-slate-600">{taskDate}</td>
                          <td className="py-2.5 px-3 border-r border-slate-200">
                            <span className="inline-block px-2 py-0.5 rounded bg-slate-100 text-slate-800 text-[8px] font-bold uppercase tracking-wider">
                              {task.type}
                            </span>
                          </td>
                          <td className="py-2.5 px-3 border-r border-slate-200 font-extrabold text-slate-900">{task.title}</td>
                          <td className="py-2.5 px-3 border-r border-slate-200 font-medium text-slate-600">{task.clientName || 'N/A'}</td>
                          <td className="py-2.5 px-3 border-r border-slate-200 text-center">
                            <span className="inline-block px-2 py-0.5 rounded-full bg-slate-100 text-slate-800 text-[8px] font-bold uppercase tracking-widest">
                              {task.status || 'N/A'}
                            </span>
                          </td>
                          <td className="py-2.5 px-3 text-slate-500 max-w-xs break-words">{task.description || 'No details.'}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={7} className="py-8 text-center text-slate-400 italic">No tasks found for this period.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Signatures */}
            <div className="grid grid-cols-3 gap-12 mt-12 pt-8 border-t border-slate-200 text-xs">
              <div>
                <p className="font-bold text-slate-400 uppercase tracking-widest text-[9px]">Prepared By (Staff)</p>
                <div className="h-16 flex items-end border-b border-slate-300 pb-1">
                  <span className="text-slate-800 font-mono italic text-sm">{previewReport.userName}</span>
                </div>
                <p className="mt-2 text-slate-700 font-bold">{previewReport.userName}</p>
                <p className="text-[10px] text-slate-400 capitalize">{previewUserRoleStr}</p>
              </div>
              <div>
                <p className="font-bold text-slate-400 uppercase tracking-widest text-[9px]">Reviewed By (Department Head)</p>
                <div className="h-16 flex items-end border-b border-slate-300 pb-1">
                </div>
                <p className="mt-2 text-slate-700 font-bold">HOD / Manager</p>
                <p className="text-[10px] text-slate-400">Operations Team</p>
              </div>
              <div>
                <p className="font-bold text-slate-400 uppercase tracking-widest text-[9px]">Approved By (Managing Director)</p>
                <div className="h-16 flex items-end border-b border-slate-300 pb-1">
                </div>
                <p className="mt-2 text-slate-700 font-bold">Managing Director</p>
                <p className="text-[10px] text-slate-400">RAMIT SOLUTIONS LTD</p>
              </div>
            </div>

            {/* Footer Notice */}
            <div className="text-center text-[9px] text-slate-400 font-medium mt-12 pt-4 border-t border-slate-100">
              Confidential Report - Generated automatically by the Performance Evaluation & Audit system. 
              Company Registered with BRELA Tanzania.
            </div>
          </div>
        ),
        document.body
      )}

      {/* 2. Email To Management Modal */}
      <AnimatePresence>
        {showEmailModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowEmailModal(null)}
              className="absolute inset-0"
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative w-full max-w-2xl bg-white rounded-3xl shadow-2xl p-6 border border-slate-100 z-10 flex flex-col max-h-[90vh]"
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between pb-4 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-50 text-purple-600 rounded-2xl flex items-center justify-center">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-black text-slate-900 tracking-tight">Email Report to Management</h3>
                    <p className="text-xs text-slate-500">Dispatch the performance audit record to organization leadership</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowEmailModal(null)}
                  className="p-2 hover:bg-slate-100 rounded-full text-slate-400 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Email Form */}
              <div className="space-y-4 my-4 overflow-y-auto pr-1 flex-1">
                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                    Recipients (comma separated)
                  </label>
                  <input
                    type="text"
                    value={emailRecipients}
                    onChange={(e) => setEmailRecipients(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 text-xs font-semibold text-slate-700"
                    placeholder="management@company.com, hr@company.com"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                    Email Subject
                  </label>
                  <input
                    type="text"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 text-xs font-semibold text-slate-700"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                    Message Body
                  </label>
                  <textarea
                    rows={10}
                    value={emailBody}
                    onChange={(e) => setEmailBody(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 text-xs font-semibold text-slate-700 font-mono leading-relaxed resize-none"
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-slate-100 shrink-0">
                <button
                  onClick={() => setShowEmailModal(null)}
                  className="flex-1 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-2xl font-bold text-xs uppercase tracking-wider transition-all cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendEmail}
                  disabled={isSendingEmail}
                  className="flex-1 py-3 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white rounded-2xl font-bold text-xs uppercase tracking-wider transition-all shadow-md shadow-purple-200 flex items-center justify-center gap-2 cursor-pointer"
                >
                  {isSendingEmail ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Dispatched...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Send Email
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
